import { flow, types } from 'mobx-state-tree'
import { Alert, Platform } from 'react-native'
import { Config } from 'react-native-config'
import { ToastPosition, ToastType } from '../../comps/Toast'
import { downloadVoiceBoxInvoice } from '../../network/downloadVoiceBoxInvoice'
import { getAllVoiceBoxes } from '../../network/getAllVoiceBoxes'
import { getSummaryV3 } from '../../network/getSummaryV3'
import { getVoiceBoxInvoices } from '../../network/getVoiceBoxInvoices'
import { updateDeviceConfigReq } from '../../network/updateDeviceConfigReq'
import {
  dateFormatter,
  DateFormatTypes,
  getErrorMessageFromResponse,
  getHHMMSS,
  getSelectedDate,
  storeData,
} from '../../util/util'
import { customError } from '../static/strings'
import { RootStore } from '../store'

const VoiceBoxModule = Platform.OS === 'web' ? null : require('mportal-module').VoiceBoxModule

const Invoice = types.model('Invoice', {
  id: types.string,
  title: types.optional(types.string, ''),
  duration: types.optional(types.string, ''),
  txnId: types.optional(types.string, ''),
  totalCharges: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  tid: types.optional(types.string, ''),
  mid: types.optional(types.string, ''),
  dsn: types.optional(types.string, ''),
  vpa: types.optional(types.string, ''),
  paidOn: types.optional(types.string, ''),
  monthlyCharges: types.optional(types.string, ''),
  gst: types.optional(types.string, ''),
})

const Details = types.model('Details', {
  id: types.string,
  tid: types.optional(types.string, ''),
  dsn: types.optional(types.string, ''),
  vpa: types.optional(types.string, ''),
  activatedOn: types.optional(types.string, ''),
  onTimeSetUpFee: types.optional(types.string, ''),
  fee: types.optional(types.string, ''),
  monthlyCharges: types.optional(types.string, ''),
  todaysSuccessfulTxnsAmt: types.optional(types.union(types.string, types.null), ''),
})

const VoiceBox = types.model('VoiceBox', {
  id: types.string,
  title: types.optional(types.string, ''),
  voiceAlertsEnabled: types.optional(types.boolean, false),
  invoices: types.optional(types.array(Invoice), []),
  details: Details,
  language: types.optional(types.string, 'English'),
})

const PairRequest = types.model('PairRequest', {
  btDevices: types.optional(types.array(types.string), []),
  dsn: types.optional(types.string, ''),
})

const VoiceBoxes = types
  .model('VoiceBoxes', {
    id: types.string,
    voiceBox: types.optional(types.array(VoiceBox), []),
    storedData: types.optional(types.string, ''),
    pairRequests: types.optional(types.array(PairRequest), []),
  })
  .actions((self) => ({
    fetchPendingPairRequest: flow(function* fetchPendingPairRequest(mid: string) {
      try {
        const pairedRequestsStr = yield VoiceBoxModule.getPendingDevices(mid)
        const pairedRequests: any[] = JSON.parse(pairedRequestsStr)
        const deviceGroupedRequests = pairedRequests.reduce((acc, item) => {
          const pRequest = acc.find((innerItem) => innerItem.dsn === item.dsn)
          if (!pRequest) {
            acc.push({
              dsn: item.dsn,
              btDevices: [item.btName],
            })
          } else {
            pRequest.btDevices.push(item.btName)
          }
          return acc
        }, [])
        const newDsns = new Set(deviceGroupedRequests.map((d: any) => d.dsn))
        self.pairRequests.filter((p) => !newDsns.has(p.dsn)).forEach((p) => self.pairRequests.remove(p))
        deviceGroupedRequests.forEach((newItem: any) => {
          const existing = self.pairRequests.find((p) => p.dsn === newItem.dsn)
          if (existing) {
            existing.btDevices.replace(newItem.btDevices)
          } else {
            self.pairRequests.push(newItem)
          }
        })
      } catch (error) {
        console.log('error fetchPendingPairRequest', error)
      }
    }),
    fetchRecentsStats: flow(function* fetchRecentsStats(deviceid: string) {
      try {
        const dateInfo = {
          fromDate: getSelectedDate(1),
          toDate: new Date(Date.now()),
          position: {
            top: 0,
            left: 0,
            visible: false,
          },
          dateLabel: 'Today',
        }
        const formattedFilteredFromData = `${dateFormatter(dateInfo.fromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          dateInfo.fromDate,
        )}`
        const filteredEndDate = dateInfo?.toDate ?? new Date(Date.now())
        const formattedFilteredToData = `${dateFormatter(filteredEndDate, DateFormatTypes.YYYYMMDD)} ${getHHMMSS(
          filteredEndDate,
        )}`
        const deviceObj = (self as any).getVoiceBox(deviceid)

        let CHANNEL_ID: any = ''
        if (Platform.OS === 'android' || Platform.OS === 'ios') {
          CHANNEL_ID = Config.REACT_APP_CHANNEL_ID ?? ''
        } else {
          CHANNEL_ID = window?._env_?.REACT_APP_CHANNEL_ID ?? ''
        }

        const txnReq = {
          inputFilter: {
            merchantId: RootStore.user.selectedMid,
            terminalId: deviceObj?.details?.tid,
            transactionType: 'PURCHASE',
            startDateTime: formattedFilteredFromData,
            endDateTime: formattedFilteredToData,
            isSMID: RootStore.user.isSmid ? 'Y' : 'N',
            channelId: CHANNEL_ID,
          },
        }
        let response
        try {
          response = yield getSummaryV3(
            RootStore.user.session.getTokens,
            RootStore.settings.metaData.mobileNumber,
            txnReq,
            'voicebox',
            false,
            dateInfo,
          )
        } catch (error) {
          response = {
            totalTransactionAmount: null,
          }
        }

        const devices = []
        self?.voiceBox?.map((device) => {
          if (device?.id === deviceid) {
            const deviceJson = JSON.parse(JSON.stringify(device))
            devices.push({
              ...JSON.parse(JSON.stringify(device)),
              details: { ...deviceJson?.details, todaysSuccessfulTxnsAmt: response?.totalTransactionAmount },
            })
          } else {
            devices.push(device)
          }
        })
        self.voiceBox = JSON.parse(JSON.stringify([...devices]))
        return response
      } catch (error) {
        console.log('error fetchRecentsStats', error)
      }
    }),
    fetchAllVoiceBoxes: flow(function* fetchAllVoiceBoxes() {
      try {
        const req = { mid: RootStore.user.selectedMid }
        const resp = yield getAllVoiceBoxes(RootStore.user.session.getTokens, req, self.storedData)
        self.voiceBox = JSON.parse(JSON.stringify(resp))
        let retObj = {}
        resp.map((item: any) => {
          retObj[item.id] = {
            isVoiceAlertEnabled: item.voiceAlertsEnabled,
            language: item.language,
          }
        })
        storeData('voiceBoxInfo', retObj)
        retObj ? (self as any).updateVoiceBoxInfo(JSON.stringify(retObj)) : (self as any).updateVoiceBoxInfo('')
        return resp
      } catch (e) {
        const message = e?.data?.error?.message ?? customError.errorOccured
        RootStore?.response?.setCustomToast({
          message: message,
          //description: 'Please try again later.',
          position: ToastPosition.BOTTOM,
          toastType: ToastType.ACTION_VIEW,
          actionLabel: 'Okay',
        })
        throw new Error(message)
      }
    }),
    fetchInvoices: flow(function* fetchInvoices(id: string) {
      try {
        const resp = yield getVoiceBoxInvoices(RootStore.user.session.getTokens, {
          mid: RootStore.user.selectedMid,
          deviceId: id,
        })

        let selectedIndex = -1
        self?.voiceBox?.find((item: any, index: number) => {
          if (item.id === id) {
            selectedIndex = index
          }
        })
        if (selectedIndex > -1) {
          self.voiceBox[selectedIndex].invoices = resp
        }
        return resp
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),
    downloadInvoicePdf: flow(function* downloadInvoicePdf(invId: string, isShare: boolean) {
      try {
        const mid = RootStore.user.selectedMid
        const tid = RootStore.user.selectedTid
        const req = {
          entity: {
            initiatingEntityId: 2103,
            application: 8006,
            type: 16,
            entityId: RootStore.user.selectedMid,
          },
          user: {
            mobile: {
              number: RootStore?.settings?.metaData?.mobileNumber ?? '',
              countryCode: '+91',
            },
            invoice: {
              externalId: invId,
            },
          },
        }
        const resp = yield downloadVoiceBoxInvoice(RootStore.user.session.getTokens, req, isShare)
        return resp
      } catch (e) {
        const error = getErrorMessageFromResponse(e)
        if (Platform.OS === 'android') {
          RootStore?.response?.setMessage(error ?? customError.errorOccured)
        } else if (typeof error === 'undefined') {
          Alert.alert('System Error', 'Error while processing request!')
        } else {
          let errorMessage = customError.errorOccured

          if (typeof error === 'string') {
            errorMessage = error
          }

          Alert.alert('System Error', errorMessage)
        }
        throw error
      }
    }),
    updateDeviceConfig: flow(function* updateDeviceConfig(id: string, isEnabled?: false, language?: string) {
      const languageMap = {
        Hindi: 'HI',
        English: 'EN',
        Telugu: 'TE',
        Tamil: 'TA',
        Malayalam: 'ML',
        Kannada: 'KN',
        Odia: 'OR',
        Bengali: 'BN',
        Marathi: 'MR',
        Punjabi: 'PA',
        Gujarati: 'GU',
      }
      try {
        const mid = RootStore.user.selectedMid
        const req = {
          mid: mid,
          dsn: id,
          enablePaymentNotification: isEnabled,
          selectedLang: isEnabled && !!language ? languageMap[language] : undefined,
        }
        const resp = yield updateDeviceConfigReq(RootStore.user.session.getTokens, req)
        return resp
      } catch (e) {
        const error = getErrorMessageFromResponse(e)
        RootStore?.response?.setToastType(ToastType.NEGATIVE)
        // RootStore?.response?.setMessage(error ?? customError.errorOccured)

        if (Platform.OS === 'android') {
          RootStore?.response?.setMessage(error ?? customError.errorOccured)
        } else if (typeof error === 'undefined') {
          Alert.alert('System Error', 'Error while processing request!')
        } else {
          let errorMessage = customError.errorOccured

          if (typeof error === 'string') {
            errorMessage = error
          }

          Alert.alert('System Error', errorMessage)
        }

        throw error
      }
    }),
    setSelectedLanguage(language: string, id: string): void {
      const retData = []
      let retItem = {}
      self?.voiceBox?.map((item: any) => {
        if (item.id == id) {
          retItem = {
            ...item,
            language: language,
          }
        } else {
          retItem = item
        }
        retData.push(retItem)
      })
      self.voiceBox = JSON.parse(JSON.stringify(retData))
    },
    updateVoiceBoxInfo(info: any) {
      self.storedData = info
    },
  }))
  .views((self) => ({
    getSelectedLanguage(id: string) {
      const voiceBox = self?.voiceBox?.find((item) => item.id === id)
      return voiceBox?.language ?? 'English'
    },
    getAllVoiceBoxes() {
      const modifiedVoiceBoxList = self.voiceBox.map((item) => {
        const isPending = self.pairRequests.some((pr) => pr.dsn === item.id)
        return {
          ...item,
          isPendingBTPairRequest: isPending,
        }
      })
      return modifiedVoiceBoxList
    },
    getVoiceBox(deviceid: string) {
      const device = self?.voiceBox?.find((item) => item.id === deviceid)
      return device ? JSON.parse(JSON.stringify(device)) : null
    },
    getAllInvoices(id: string) {
      if (!id) {
        return
      }
      const voiceBox = self?.voiceBox?.find((item) => item.id === id)
      return JSON.parse(JSON.stringify(voiceBox?.invoices)) ?? []
    },
    getInvoice(dsn: string, invId: string) {
      if (!dsn || !invId) {
        return
      }
      const voiceBox = self?.voiceBox?.find((item) => item?.id === dsn)
      const invoice = voiceBox?.invoices?.find((item) => item?.id === invId)
      const data = {
        id: invoice.id,
        tid: invoice.tid,
        mid: invoice.mid,
        dsn: dsn,
        VPA: invoice.vpa,
        paidOn: invoice.paidOn,
        monthlyCharges: `₹ ${invoice.monthlyCharges}`,
        gst: `₹${invoice.gst}`,
        totalCharges: `₹${invoice.totalCharges}`,
      }
      return data
    },
    getVoiceBoxStoredData() {
      return self.storedData
    },
  }))

const VoiceBoxesData = VoiceBoxes.create({
  id: '',
  voiceBox: [],
  storedData: '',
})

export { VoiceBoxes, VoiceBoxesData }
